import * as Yup from "yup";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik, Form, FormikProvider } from "formik";
// material
import {
  Stack,
  TextField,
  IconButton,
  InputAdornment,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
// component
import Iconify from "../../components/Iconify";
import instance from "../../api/axiosRequest";
import Message from "../../components/Message";
import BackButton from "../../components/BackButton";

// ----------------------------------------------------------------------

export default function CreateUser() {
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [details, setDetails] = useState({
    severity: "success",
    message: ""
  });

  useEffect(() => {
    const getCategories = async () => {
      try {
        const { data } = await instance.get("/admin/listVendorCategories");
        setCategories(data.data.rows);
      } catch (error) {
        console.log(error);
      }
    };
    getCategories();
  }, []);

  const VendorSchema = Yup.object().shape({
    email: Yup.string()
      .email("Email must be a valid email address")
      .required("Email is required"),
    password: Yup.string().required("Password is required"),
    country_code: Yup.string().required("Country code is required"),
    phone: Yup.string().required("Phone is required"),
    name: Yup.string().required("Name is required"),
    vendor_category_id: Yup.string().required("Category is required")
  });

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      name: "",
      country_code: "",
      phone: "",
      vendor_category_id: ""
    },
    validationSchema: VendorSchema,
    onSubmit: async (user) => {
      try {
        await instance.post("/admin/addVendor", user);
        navigate("/dashboard/clients", { replace: true });
      } catch (error) {
        setDetails({ severity: "error", message: error.response.data.message });
        setOpen(true);
      }
    }
  });

  const { errors, touched, isSubmitting, values, handleSubmit, getFieldProps } =
    formik;

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  return (
    <Box sx={{ px: 4, width: 720 }}>
      <BackButton />
      <Typography variant="h2">Add new User</Typography>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <Stack spacing={3} mb={3}>
            <TextField
              fullWidth
              autoComplete="username"
              label="Name"
              {...getFieldProps("name")}
              error={Boolean(touched.name && errors.name)}
              helperText={touched.name && errors.name}
            />
            <TextField
              fullWidth
              autoComplete="email"
              type="email"
              label="Email address"
              {...getFieldProps("email")}
              error={Boolean(touched.email && errors.email)}
              helperText={touched.email && errors.email}
            />
            <TextField
              fullWidth
              label="Country Code"
              {...getFieldProps("country_code")}
              error={Boolean(touched.country_code && errors.country_code)}
              helperText={touched.country_code && errors.country_code}
            />
            <TextField
              fullWidth
              label="Phone"
              {...getFieldProps("phone")}
              error={Boolean(touched.phone && errors.phone)}
              helperText={touched.phone && errors.phone}
            />
            <TextField
              fullWidth
              autoComplete="current-password"
              type={showPassword ? "text" : "password"}
              label="Password"
              {...getFieldProps("password")}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleShowPassword} edge="end">
                      <Iconify
                        icon={
                          showPassword ? "eva:eye-fill" : "eva:eye-off-fill"
                        }
                      />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              error={Boolean(touched.password && errors.password)}
              helperText={touched.password && errors.password}
            />

            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">
                Vendor Category
              </InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                label="Vendor Category"
                value={values.vendor_category_id}
                {...getFieldProps("vendor_category_id")}
              >
                {categories.map((e) => (
                  <MenuItem key={e.id} value={e.id}>
                    {e.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting}
          >
            Submit
          </LoadingButton>
        </Form>
        <Message open={open} setOpen={setOpen} details={details} />
      </FormikProvider>
    </Box>
  );
}
